/**
 * 页面滚动进度条
 * */
import { useDebounceFn, useEventListener } from '@vueuse/core'
import {
  debugWarn,
  getScrollContainer,
  getScrollDistance,
  getScrollLength,
} from '@element-plus/utils'
import { useProgressBar } from './useProgressBar'
import type { ProgressBarCfg } from './useProgressBarTypes'
import type {
  PartialProgressBarReturn,
  ScrollProgressBarCfg,
  ScrollProgressBarReturn,
} from './useScrollProgressBarTypes'

const defaultTarget = (): Window => window
const defaultDebounce = 0
const defaultDelay = 1500

const defaultScrollConfig = {
  duration: 0,
  showText: false,
  strokeWidth: 10,
  visible: false,
}

const normalizeTarget = (
  target?: ScrollProgressBarCfg['target']
): HTMLElement | Window => {
  const baseTarget = (t: string | HTMLElement): HTMLElement | Window => {
    if (typeof t === 'string') {
      const e = window.document.querySelector(t)
      return e ? (e as HTMLElement) : defaultTarget()
    }
    return t
  }

  if (typeof target === 'function') {
    return baseTarget(target())
  }
  return target ? baseTarget(target) : defaultTarget()
}

const normilizeSteps = (
  steps: number | number[] = 0,
  l = 0,
  last = false
): string[] => {
  let result: string[] = []
  if (l) {
    if (Array.isArray(steps) && steps.length) {
      for (const step of steps) {
        const ratio = Number.parseFloat(((step * 100) / l).toFixed(8))
        result.push(`${ratio}%`)
      }
      if (last) {
        result.push(`100%`)
      }
      result = result.filter((step) => !!step && Number.parseFloat(step))
      result.sort((a, b) => Number.parseFloat(a) - Number.parseFloat(b))
    } else if (typeof steps === 'number' && steps) {
      const count = Math.ceil(steps)
      const per = Number.parseFloat((100 / count).toFixed(8))
      for (let i = 1; i <= count; i++) {
        result.push(`${per * i}%`)
      }
    }
  }
  return result
}

const calcPercentage = (d = 0, l = 0): number => {
  if (l === 0) return 0
  return (d / l) * 100
}

const calcStepPercentage = (d = 0, l = 0, steps: string[] = []): number => {
  if (l === 0 || !steps.length) return 0
  const distances = steps.map((item) => (Number.parseFloat(item) / 100) * l)
  let t = -1
  for (const c of distances) {
    if (c <= d) {
      t += 1
    } else {
      break
    }
  }
  return t === -1 ? 0 : Number.parseFloat(steps[t])
}

export function useScrollProgressBar(
  config?: ScrollProgressBarCfg
): ScrollProgressBarReturn {
  const scrollCfg = config
    ? ({ ...config, ...defaultScrollConfig } as ProgressBarCfg)
    : ({ ...defaultScrollConfig } as ProgressBarCfg)

  let progressBar: PartialProgressBarReturn = {}
  let cleanup: (() => void) | undefined = undefined
  let hideTimeout: ReturnType<typeof setTimeout> | number = -1

  const direction = config?.direction ?? 'vertical'
  const isVertical = direction === 'vertical'
  const target = normalizeTarget(config?.target) as HTMLElement
  const scrollContainer = getScrollContainer(target, isVertical)

  const hideDelay = config?.hideDelay ?? defaultDelay
  const debounce = config?.debounce ?? defaultDebounce
  const steps: string[] = normilizeSteps(
    config?.steps,
    getScrollLength(target, isVertical),
    config?.autoSupplyLast
  )
  const stepScroll = !!(steps && steps.length)
  const showSteps = config?.showSteps ?? true
  const hideTrigger = config?.hideTrigger ?? (stepScroll ? 'never' : 'always')
  const neverHide = hideTrigger === 'never'
  const alwaysHide = hideTrigger === 'always'
  const progressHide = hideTrigger === 'progress'

  const destroy = (): void => {
    if (hideTimeout !== -1) {
      clearTimeout(hideTimeout)
    }
    cleanup && cleanup()
    progressBar && progressBar.destroy?.()
  }

  const handleScroll = (): void => {
    if (hideTimeout !== -1) {
      clearTimeout(hideTimeout)
      hideTimeout = -1
    }
    // 计算滚动占比 => 进度
    let percentage = 0
    const length = getScrollLength(target, isVertical)
    const distance = getScrollDistance(target, isVertical)

    // 根据滚动方式-计算进度
    if (stepScroll) {
      percentage = calcStepPercentage(distance, length, steps)
    } else {
      percentage = calcPercentage(distance, length)
    }

    progressBar && progressBar.update?.(percentage)

    // 根据隐藏策略控制显示
    if (neverHide || percentage !== 0) {
      progressBar && progressBar.vm?.show()
      if (alwaysHide || (progressHide && percentage < 100)) {
        hideTimeout = setTimeout(() => {
          progressBar && progressBar.vm?.hide()
        }, hideDelay)
      }
    } else {
      progressBar && progressBar.vm?.hide()
    }
  }

  if (scrollContainer) {
    if (stepScroll && showSteps) {
      scrollCfg.steps = steps
    }
    progressBar = useProgressBar(scrollCfg)
    cleanup = useEventListener(
      scrollContainer,
      'scroll',
      useDebounceFn(handleScroll, debounce)
    )
    // default call scroll
    handleScroll()
  } else {
    debugWarn('useScrolProgressBar', 'can not find scrol container by target')
  }

  return {
    ...progressBar,
    cleanup,
    destroy,
    scrollContainer,
  }
}
